import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import type { ChatSummary, MessageInput, MessagePart } from "@/interfaces/Chat";
import { useChatNotifications, ChatNotificationState } from "@/hooks/useChatNotifications";
import { useChatSync } from "@/hooks/useChatSync";
import {
  clearChatSession,
  purgeChatConversation,
  refreshChat,
} from "@/services/chatSession";
import type { RootState, AppDispatch } from "@/store";
import { googleMeetApi, meetOperationFrom } from "@/services/googleMeet";
import type { ChatMeetState, MeetOperationView } from "@/interfaces/GoogleMeet";

export type ChatDraft = {
  parts: MessagePart[];
  replyToId?: string;
  reply?: { id: string; authorName: string; excerpt: string };
};
type Submission = {
  actorId: string;
  generation: number;
  version: number;
  draft: ChatDraft;
  body: MessageInput;
  pending: boolean;
};
type ChatContextValue = ReturnType<typeof useChatSync> & {
  actorId: string | null;
  notifications: ChatNotificationState;
  drafts: Record<string, ChatDraft>;
  setDraft: (id: string, draft: ChatDraft) => void;
  clearDraft: (id: string) => void;
  beginSubmission: (id: string, draft: ChatDraft) => MessageInput | null;
  finishSubmission: (
    id: string,
    body: MessageInput,
    success: boolean
  ) => boolean;
  submitting: Record<string, boolean>;
  meetOperations: Record<string, ChatMeetState>;
  createMeet: (id: string) => Promise<void>;
  summary: ChatSummary | null;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  purgeConversation: (id: string) => void;
};
const ChatContext = createContext<ChatContextValue | null>(null);
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error("useChat requires ChatProvider");
  return context;
};
type DraftEntry = { draft: ChatDraft; version: number };

/** Memory-only session shell and the single mounted synchronization owner. */
export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const actorId = useSelector(
    (state: RootState) => state.auth.user?.id ?? null
  );
  const { generation, blocked, conversationVersions } = useSelector(
    (state: RootState) => state.chatSession
  );
  const [local, setLocal] = useState<{
    actorId: string | null;
    generation: number;
    drafts: Record<string, DraftEntry>;
    active: { id: string; version: number } | null;
  }>({ actorId, generation, drafts: {}, active: null });
  const meetEntries = useRef<Record<string, ChatMeetState>>({});
  const [meetRevision, setMeetRevision] = useState(0);
  Object.keys(meetEntries.current).forEach((id) => {
    const entry = meetEntries.current[id];
    if (
      blocked ||
      entry.actorId !== actorId ||
      entry.generation !== generation ||
      entry.version !== (conversationVersions[id] ?? 0)
    )
      delete meetEntries.current[id];
  });
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const submissions = useRef<Record<string, Submission>>({});
  const [submissionRevision, setSubmissionRevision] = useState(0);
  Object.keys(submissions.current).forEach((id) => {
    const entry = submissions.current[id];
    if (
      blocked ||
      entry.actorId !== actorId ||
      entry.generation !== generation ||
      entry.version !== (conversationVersions[id] ?? 0)
    )
      delete submissions.current[id];
  });
  const validSession =
    local.actorId === actorId && local.generation === generation && !blocked;

  useEffect(() => {
    // Keyed AuthMiddleware remounts this provider. The generation also covers
    // access loss/logout and consumers that mount a provider without that key.
    setLocal((previous) =>
      previous.actorId === actorId && previous.generation === generation
        ? previous
        : { actorId, generation, drafts: {}, active: null }
    );
  }, [actorId, generation]);
  useEffect(
    () => () => {
      if (actorId) dispatch(clearChatSession({ actorId }));
    },
    [actorId, dispatch]
  );

  useEffect(() => {
    setLocal((previous) => ({
      ...previous,
      drafts: Object.fromEntries(
        Object.entries(previous.drafts).filter(
          ([id, entry]) => entry.version === (conversationVersions[id] ?? 0)
        )
      ),
      active:
        previous.active &&
        previous.active.version ===
          (conversationVersions[previous.active.id] ?? 0)
          ? previous.active
          : null,
    }));
  }, [conversationVersions]);
  const sessionIsCurrent = useCallback(() => {
    const state = store.getState();
    return (
      state.auth.user?.id === actorId &&
      state.chatSession.generation === generation &&
      !state.chatSession.blocked
    );
  }, [store, actorId, generation]);
  const meetIsCurrent = useCallback(
    (entry: ChatMeetState) =>
      mounted.current &&
      sessionIsCurrent() &&
      meetEntries.current[entry.conversationId] === entry &&
      entry.version ===
        (store.getState().chatSession.conversationVersions[
          entry.conversationId
        ] ?? 0),
    [sessionIsCurrent, store]
  );
  const finishMeet = useCallback(
    (
      entry: ChatMeetState,
      operation: MeetOperationView | null,
      error?: unknown
    ) => {
      if (!meetIsCurrent(entry)) return;
      entry.busy = false;
      if (operation) {
        if (operation.code === "CHAT_ACCOUNT_INACTIVE") {
          dispatch(clearChatSession({ actorId: entry.actorId, blocked: true }));
          return;
        }
        // These operation-level failures describe a write right lost during Google work.
        if (
          ["CHAT_NOT_FOUND", "CHAT_FORBIDDEN"].includes(operation.code ?? "")
        ) {
          dispatch(
            purgeChatConversation({
              actorId: entry.actorId,
              conversationId: entry.conversationId,
            })
          );
          return;
        }
        entry.operation = operation;
        entry.error = null;
        if (operation.status === "completed") dispatch(refreshChat());
        if (
          [
            "MEET_UNCONFIGURED",
            "MEET_RECONNECT_REQUIRED",
            "MEET_CONNECTION_CHANGED",
          ].includes(operation.code ?? "")
        )
          dispatch(googleMeetApi.util.invalidateTags(["MeetStatus"]));
      } else {
        const failure = error as { status?: number | string } | undefined;
        if (failure?.status === 403 || failure?.status === 404) {
          dispatch(
            purgeChatConversation({
              actorId: entry.actorId,
              conversationId: entry.conversationId,
            })
          );
          return;
        }
        entry.error =
          "La réponse n’a pas pu être confirmée. Réessayez pour retrouver la même demande.";
      }
      setMeetRevision((v) => v + 1);
    },
    [dispatch, meetIsCurrent]
  );
  const createMeet = useCallback(
    async (id: string) => {
      if (
        !actorId ||
        !sessionIsCurrent() ||
        (store.getState().chatSession.conversationVersions[id] ?? 0) !==
          (conversationVersions[id] ?? 0)
      )
        return;
      const previous = meetEntries.current[id];
      if (
        previous?.busy ||
        (previous?.operation?.status === "pending" &&
          !previous.error &&
          previous.operation.code !== "MEET_PUBLICATION_RETRY_REQUIRED")
      )
        return;
      // Only a deliberate action after a terminal state allocates another key.
      const reuse =
        previous &&
        (!previous.operation || previous.operation.status === "pending");
      const entry: ChatMeetState = reuse
        ? previous
        : {
            actorId,
            generation,
            version: conversationVersions[id] ?? 0,
            conversationId: id,
            clientId: crypto.randomUUID(),
            operation: null,
            busy: false,
            error: null,
          };
      entry.busy = true;
      entry.error = null;
      meetEntries.current[id] = entry;
      setMeetRevision((v) => v + 1);
      const request = dispatch(
        googleMeetApi.endpoints.createChatMeet.initiate({
          actorId,
          conversationId: id,
          clientId: entry.clientId,
        })
      );
      try {
        finishMeet(entry, meetOperationFrom(await request.unwrap()));
      } catch (error) {
        finishMeet(
          entry,
          meetOperationFrom((error as { data?: unknown }).data),
          error
        );
      } finally {
        request.reset();
      }
    },
    [
      actorId,
      generation,
      conversationVersions,
      sessionIsCurrent,
      store,
      dispatch,
      finishMeet,
    ]
  );
  const meetOperations = useMemo(
    () => ({ ...meetEntries.current }),
    // Ref contents change through the revision counter and session purges.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetRevision, actorId, generation, blocked, conversationVersions]
  );
  const setDraft = useCallback(
    (id: string, draft: ChatDraft) => {
      if (
        !actorId ||
        !sessionIsCurrent() ||
        (store.getState().chatSession.conversationVersions[id] ?? 0) !==
          (conversationVersions[id] ?? 0)
      )
        return;
      setLocal((previous) => ({
        actorId,
        generation,
        drafts: {
          ...(previous.actorId === actorId && previous.generation === generation
            ? previous.drafts
            : {}),
          [id]: { draft, version: conversationVersions[id] ?? 0 },
        },
        active:
          previous.actorId === actorId && previous.generation === generation
            ? previous.active
            : null,
      }));
    },
    [actorId, generation, sessionIsCurrent, conversationVersions, store]
  );
  const clearDraft = useCallback(
    (id: string) => {
      if (
        !sessionIsCurrent() ||
        (store.getState().chatSession.conversationVersions[id] ?? 0) !==
          (conversationVersions[id] ?? 0)
      )
        return;
      delete submissions.current[id];
      setSubmissionRevision((v) => v + 1);
      setLocal((previous) => {
        const drafts = { ...previous.drafts };
        delete drafts[id];
        return { ...previous, drafts };
      });
    },
    [sessionIsCurrent, store, conversationVersions]
  );
  const beginSubmission = useCallback(
    (id: string, draft: ChatDraft): MessageInput | null => {
      const version = conversationVersions[id] ?? 0;
      if (
        !actorId ||
        !sessionIsCurrent() ||
        (store.getState().chatSession.conversationVersions[id] ?? 0) !== version
      )
        return null;
      const previous = submissions.current[id];
      if (previous?.pending) return null;
      const body =
        previous && previous.draft === draft
          ? previous.body
          : {
              clientId: crypto.randomUUID(),
              parts: draft.parts.map((part) => ({ ...part })),
              ...(draft.replyToId ? { replyToId: draft.replyToId } : {}),
            };
      submissions.current[id] = {
        actorId,
        generation,
        version,
        draft,
        body,
        pending: true,
      };
      setSubmissionRevision((v) => v + 1);
      return body;
    },
    [actorId, generation, conversationVersions, sessionIsCurrent, store]
  );
  const finishSubmission = useCallback(
    (id: string, body: MessageInput, success: boolean) => {
      const entry = submissions.current[id];
      if (
        !entry ||
        entry.body !== body ||
        !sessionIsCurrent() ||
        entry.actorId !== actorId ||
        entry.generation !== generation ||
        entry.version !==
          (store.getState().chatSession.conversationVersions[id] ?? 0)
      )
        return false;
      entry.pending = false;
      if (success) {
        delete submissions.current[id];
        setLocal((previous) => {
          if (previous.drafts[id]?.draft !== entry.draft) return previous;
          const drafts = { ...previous.drafts };
          delete drafts[id];
          return { ...previous, drafts };
        });
      }
      setSubmissionRevision((v) => v + 1);
      return true;
    },
    [actorId, generation, sessionIsCurrent, store]
  );
  const submitting = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(submissions.current).map(([id, entry]) => [
          id,
          entry.pending,
        ])
      ),
    // Include the session identity so a render cannot expose the previous actor's pending state.
    [submissionRevision, actorId, generation, conversationVersions, blocked]
  );
  const setActiveConversationId = useCallback(
    (id: string | null) => {
      if (!actorId || !sessionIsCurrent()) return;
      setLocal((previous) => ({
        actorId,
        generation,
        drafts:
          previous.actorId === actorId && previous.generation === generation
            ? previous.drafts
            : {},
        active: id ? { id, version: conversationVersions[id] ?? 0 } : null,
      }));
    },
    [actorId, generation, sessionIsCurrent, conversationVersions]
  );
  const purgeConversation = useCallback(
    (conversationId: string) => {
      if (actorId && sessionIsCurrent())
        dispatch(purgeChatConversation({ actorId, conversationId }));
    },
    [actorId, dispatch, sessionIsCurrent]
  );
  const drafts = useMemo(() => {
    const visible: Record<string, ChatDraft> = {};
    if (validSession)
      Object.entries(local.drafts).forEach(([id, entry]) => {
        if (entry.version === (conversationVersions[id] ?? 0))
          visible[id] = entry.draft;
      });
    return visible;
  }, [validSession, local.drafts, conversationVersions]);
  const activeConversationId =
    validSession &&
    local.active &&
    local.active.version === (conversationVersions[local.active.id] ?? 0)
      ? local.active.id
      : null;
  // Only the selected conversation polls; navigation keeps its original key in memory.
  useEffect(() => {
    const entry = activeConversationId
      ? meetEntries.current[activeConversationId]
      : undefined;
    if (
      !entry ||
      entry.busy ||
      entry.error ||
      entry.operation?.status !== "pending" ||
      entry.operation.code === "MEET_PUBLICATION_RETRY_REQUIRED"
    )
      return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let request:
      | ReturnType<
          ReturnType<
            typeof googleMeetApi.endpoints.getChatMeetOperation.initiate
          >
        >
      | undefined;
    const visible = () => !document.hidden && navigator.onLine !== false;
    const stop = () => {
      if (timer) clearTimeout(timer);
      timer = undefined;
    };
    const poll = async () => {
      stop();
      if (disposed || request || !visible() || !meetIsCurrent(entry)) return;
      const currentRequest = dispatch(
        googleMeetApi.endpoints.getChatMeetOperation.initiate(
          {
            actorId: entry.actorId,
            conversationId: entry.conversationId,
            operationId: entry.operation!.id,
          },
          { subscribe: false, forceRefetch: true }
        )
      );
      request = currentRequest;
      try {
        const operation = await currentRequest.unwrap();
        if (!disposed && visible())
          finishMeet(entry, meetOperationFrom(operation));
      } catch (error) {
        if (!disposed && visible())
          finishMeet(
            entry,
            meetOperationFrom((error as { data?: unknown }).data),
            error
          );
      } finally {
        currentRequest.unsubscribe();
        request = undefined;
      }
    };
    const visibility = () => {
      stop();
      if (visible()) void poll();
    };
    if (visible()) timer = setTimeout(poll, 3000);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("online", visibility);
    window.addEventListener("offline", visibility);
    return () => {
      disposed = true;
      stop();
      request?.abort();
      request?.unsubscribe();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("online", visibility);
      window.removeEventListener("offline", visibility);
    };
  }, [activeConversationId, meetRevision, meetIsCurrent, finishMeet, dispatch]);
  const sync = useChatSync({
    actorId,
    conversationId: activeConversationId,
    onChanges: () => {},
    onAccessLost: () => {},
  });
  const notifications = useChatNotifications(activeConversationId);
  const summary = sync.summary;
  const value = useMemo(
    () => ({
      ...sync,
      notifications,
      actorId,
      drafts,
      setDraft,
      clearDraft,
      beginSubmission,
      finishSubmission,
      submitting,
      meetOperations,
      createMeet,
      summary: !blocked ? (summary ?? null) : null,
      activeConversationId,
      setActiveConversationId,
      purgeConversation,
    }),
    [
      sync,
      notifications,
      actorId,
      drafts,
      setDraft,
      clearDraft,
      beginSubmission,
      finishSubmission,
      submitting,
      meetOperations,
      createMeet,
      summary,
      blocked,
      activeConversationId,
      setActiveConversationId,
      purgeConversation,
    ]
  );
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
